import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useKeycloak } from "@react-keycloak/web";
import { type FC, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router";
import { useApi } from "../api/APIContext";
import { ContentContainer } from "../components/content/ContentContainer";
import { PageHeader } from "../components/content/PageHeader";
import { Button } from "../components/form/Button";
import { InspectionQuestionsList } from "../components/inspections/InspectionQuestionsList";
import { useEnrichedInspection } from "../components/inspections/useEnrichedInspections";
import { Loading } from "../components/Loading";
import { useModal } from "../components/layout/ModalProvider";
import { OtherAssessorWarningModal } from "../modals/OtherAssessorWarningModal";
import { findOtherAssessor } from "../util/otherAssessor";

export const InspectionQuestionsPage: FC = () => {
  const { inspectionId } = useParams();
  const navigate = useNavigate();
  const { loading, assessors } = useApi();
  const { keycloak } = useKeycloak();
  const enrichedInspection = useEnrichedInspection(inspectionId);

  // Warn once per opened inspection when the logged-in user isn't its assessor, to prevent filling in the wrong
  // inspection by accident. Only a warning: the user can always continue.
  const otherAssessor = loading
    ? null
    : findOtherAssessor(enrichedInspection?.assessorId, assessors, keycloak.tokenParsed?.email);
  const otherAssessorModal = useModal({
    title: "Andere assessor",
    body: otherAssessor && (
      <OtherAssessorWarningModal assessor={otherAssessor} onBack={() => navigate(`/inspections/${inspectionId}`)} />
    ),
  });
  const warnedForInspectionId = useRef<string | null>(null);
  useEffect(() => {
    if (!otherAssessor || !inspectionId || warnedForInspectionId.current === inspectionId) return;
    warnedForInspectionId.current = inspectionId;
    otherAssessorModal.open();
  }, [otherAssessor, inspectionId, otherAssessorModal]);

  return (
    <ContentContainer size={"lg"}>
      <Loading loading={loading || !enrichedInspection?.questionSet}>
        {enrichedInspection && (
          <div>
            <Button
              type={"secondary"}
              icon={faArrowLeft}
              size={"md"}
              text={"Terug naar inspectie"}
              onClick={() => navigate(`/inspections/${enrichedInspection.id}`)}
            />
            <div className={"flex items-baseline justify-between flex-wrap gap-2"}>
              <PageHeader>Vragen {enrichedInspection.name ?? ""}</PageHeader>
              <span className={"text-sm text-slate-500 mb-8"}>
                {enrichedInspection.questionSetName} &middot; {enrichedInspection.companyName}
              </span>
            </div>
            <InspectionQuestionsList inspection={enrichedInspection} />
          </div>
        )}
      </Loading>
    </ContentContainer>
  );
};
